import { Button } from "$components/button/button";
import { CrossIcon } from "$components/icons";
import type RHFAutocomplete from "$components/rhf-fields/fields/Autocomplete";
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputBase,
  InputLabel,
  Popper,
  Typography,
} from "@material-ui/core";
import { styled } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import { useAutocomplete } from "@material-ui/lab";
import React, {
  type MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useController } from "react-hook-form";

enum KeyboardEventKeys {
  Backspace = "Backspace",
  ArrowDown = "ArrowDown",
  ArrowUp = "ArrowUp",
  Enter = "Enter",
  Escape = "Escape",
}

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2.5),
}));

const TextAreaContainer = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  backgroundColor: theme.palette.grey[200],
  border: "2px solid transparent",
  borderRadius: 8,
  padding: theme.spacing(2),
  transition:
    "border-color 0.2s ease-in-out, background-color 0.3s ease-in-out",
  "&:focus-within": {
    backgroundColor: theme.palette.background.default,
    borderColor: theme.palette.primary.main,
  },
}));

const ChipContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  marginTop: theme.spacing(1),
  paddingTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
  gap: theme.spacing(1.5),
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  height: 44,
  fontWeight: 700,
  fontSize: 16,
  gap: 8,
  borderRadius: 4,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  // prevent background colour change when chip is clicked
  "&:focus": {
    backgroundColor: theme.palette.primary.main,
  },
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
  minWidth: "120px",
  width: "100%",
  border: "none",
  outline: "none",
  fontSize: 16,
  backgroundColor: "transparent",
  color: theme.palette.text.primary,
  overflow: "wrap",
  transition: "all 0.3s ease-in-out",
  "& .MuiInputBase-input": {
    paddingTop: theme.spacing(1),
  },
}));

const StyledInputLabel = styled(InputLabel)(({ theme }) => ({
  color: "#222222",
  fontSize: 16,
  position: "absolute",
  transition: "all 0.2s ease-in-out",
  pointerEvents: "none",
  transform: "translate(0, 22px) scale(1)",
  top: 4,
  "&.MuiFormLabel-root.Mui-focused, &.MuiInputLabel-shrink": {
    transform: "translate(0, 8px) scale(0.75)",
  },
  "&.MuiFormLabel-root.Mui-focused": {
    color: theme.palette.primary.main,
  },
}));

const AvailableOptionsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));

const SeeMoreButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(1),
}));

const DropdownContainer = styled("ul")(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
  borderRadius: 4,
  marginTop: 4,
  height: "auto",
  maxHeight: "320px",
  overflowY: "auto",
  padding: `${theme.spacing(3)} ${theme.spacing(3)} 14px 14px`,
}));

const OptionItem = styled("li")(({ theme }) => ({
  width: "100%",
  padding: "10px",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  '&[data-focused="true"]': {
    backgroundColor: theme.palette.action.selected,
  },
}));

export interface ChipOption {
  optionId: string;
  label: string;
  index?: number;
  keywords?: string[];
}

const MemoizedChip = React.memo(
  ({ field, onDelete }: { field: ChipOption; onDelete: () => void }) => {
    return (
      <StyledChip
        key={field.optionId}
        label={field.label}
        onDelete={onDelete}
        deleteIcon={<CrossIcon data-testid="chipDeleteButton" />}
      />
    );
  },
);

const AutocompleteInput = ({
  options,
  chips,
  addChip,
  removeChip,
  placeholder,
  containerRef,
  onDropdownOptionSelected,
  ...restProps
}: {
  options: ChipOption[];
  chips: ChipOption[];
  addChip: (chip: ChipOption) => void;
  removeChip: (index: number) => void;
  placeholder?: string;
  onDropdownOptionSelected?: (option: ChipOption) => void;
  containerRef: MutableRefObject<HTMLDivElement>;
} & React.ComponentProps<typeof InputBase>) => {
  const [inputValue, setInputValue] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [popupOpen, setPopupOpen] = useState(false);
  const {
    getRootProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
  } = useAutocomplete({
    options: options.filter(
      (option) => !chips.some((chip) => chip.optionId === option.optionId),
    ),
    inputValue,
    onInputChange: (_, value) => {
      setInputValue(value);
      setPopupOpen(true);
    },
    onOpen: () => {
      setPopupOpen(true);
    },
    onClose: () => {
      setPopupOpen(false);
    },
    getOptionLabel: (option) => option.label,
    filterOptions: (options, state) => {
      const unselectedOptions = options.filter(
        (option) => !chips.some((chip) => chip.optionId === option.optionId),
      );

      if (!state.inputValue) {
        return unselectedOptions.sort((a, b) => a.label.localeCompare(b.label));
      }

      const filtered = unselectedOptions.filter(
        (option) =>
          option.label.toLowerCase().includes(state.inputValue.toLowerCase()) ||
          option.keywords?.some((kw) =>
            kw.toLowerCase().includes(state.inputValue.toLowerCase()),
          ),
      );

      return filtered.sort((a, b) => a.label.localeCompare(b.label));
    },
  });

  const inputProps = {
    ...restProps,
    ...getInputProps(),
    value: inputValue,
    onKeyDown: (event: React.KeyboardEvent) => {
      if (
        event.key === "Backspace" &&
        (event.target as HTMLInputElement).value === "" &&
        chips.length > 0
      ) {
        removeChip(chips.length - 1);
        return;
      }

      if (event.key === KeyboardEventKeys.Enter) {
        event.preventDefault();
        if (
          groupedOptions.length > 0 &&
          highlightedIndex >= 0 &&
          highlightedIndex < groupedOptions.length
        ) {
          addChip(groupedOptions[highlightedIndex] as ChipOption);
          setInputValue("");
          setHighlightedIndex(-1);
          setPopupOpen(false);

          // Stop event propagation to prevent other handlers from triggering
          event.stopPropagation();
        }
        return;
      }

      if (groupedOptions.length > 0) {
        switch (event.key) {
          case KeyboardEventKeys.ArrowDown:
            setPopupOpen(true);
            event.preventDefault();
            setHighlightedIndex((prev) =>
              prev < groupedOptions.length - 1 ? prev + 1 : 0,
            );
            break;
          case KeyboardEventKeys.ArrowUp:
            setPopupOpen(true);
            event.preventDefault();
            setHighlightedIndex((prev) =>
              prev > 0 ? prev - 1 : groupedOptions.length - 1,
            );
            break;
          case KeyboardEventKeys.Escape:
            event.preventDefault();
            setHighlightedIndex(-1);
            setPopupOpen(false);
            break;
        }
      }
    },
    placeholder: chips.length ? "or type to search" : "",
  };

  return (
    <div {...getRootProps()} style={{ width: "100%" }}>
      <StyledInput id="chip-input" {...inputProps} />
      {popupOpen && groupedOptions.length > 0 && containerRef.current && (
        <Popper
          open
          anchorEl={containerRef.current}
          placement="bottom-start"
          style={{
            width: containerRef.current.clientWidth,
          }}
          popperOptions={{
            modifiers: { flip: { enabled: false } },
          }}
        >
          <DropdownContainer {...getListboxProps()}>
            {groupedOptions.map((option, index) => (
              <OptionItem
                {...getOptionProps({ option, index })}
                key={option.optionId}
                onClick={() => {
                  onDropdownOptionSelected?.(option as ChipOption);
                  addChip(option as ChipOption);
                  setInputValue("");
                  setPopupOpen(false);
                }}
                data-focused={highlightedIndex === index}
              >
                <Typography variant="body1">{option.label}</Typography>
              </OptionItem>
            ))}
          </DropdownContainer>
        </Popper>
      )}
    </div>
  );
};

export const TextInputWithSelectableOptions: React.FC<
  {
    chipsFieldName: string;
    availableOptions: ChipOption[];
    chips: ChipOption[];
    addChip: (chip: ChipOption) => void;
    removeChip: (index: number) => void;
    loading?: boolean;
    nudge?: React.ReactNode;
    onRecommendedVaccineSelected?: (option: ChipOption) => void;
  } & (
    | { textFieldName: string; searchProps?: never }
    | {
        textFieldName?: never;
        searchProps: Omit<
          Parameters<typeof RHFAutocomplete>[0],
          "name" | "renderInput"
        > & {
          options: ChipOption[];
          onDropdownOptionSelected?: (option: ChipOption) => void;
        };
      }
  ) &
    React.ComponentProps<typeof InputBase>
> = ({
  textFieldName,
  chipsFieldName,
  availableOptions,
  placeholder,
  chips,
  addChip,
  removeChip,
  loading = false,
  nudge,
  searchProps,
  onRecommendedVaccineSelected,
  ...restProps
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { field: textField } = useController({
    name: textFieldName ?? "",
  });

  const [showAllAvailableOptions, setShowAllAvailableOptions] = useState(false);

  useEffect(() => {
    if (chips.length === 0) {
      setShowAllAvailableOptions(false);
    }
  }, [chips.length]);

  const toggleShowAllAvailableOptions = () =>
    setShowAllAvailableOptions((prev) => !prev);

  const displayedAvailableOptions = useMemo(() => {
    return showAllAvailableOptions
      ? availableOptions
      : availableOptions.slice(0, 3);
  }, [showAllAvailableOptions, availableOptions]);

  const hasExtraAvailableOptions = availableOptions.length > 3;

  const onDelete = useCallback(
    (index: number) => () => removeChip(index),
    [removeChip],
  );

  return (
    <FormControl variant="standard" fullWidth>
      <Container>
        <TextAreaContainer ref={containerRef}>
          <StyledInputLabel
            shrink={textField?.value?.length > 0 || chips.length > 0}
            htmlFor="chip-input"
          >
            {placeholder}
          </StyledInputLabel>

          {!!chips.length && (
            <ChipContainer>
              {chips.map((field, index) => (
                <MemoizedChip
                  key={field.optionId}
                  field={field}
                  onDelete={onDelete(index)}
                />
              ))}
            </ChipContainer>
          )}

          {searchProps?.options.length > 0 ? (
            <AutocompleteInput
              options={searchProps.options}
              chips={chips}
              addChip={addChip}
              removeChip={removeChip}
              placeholder={placeholder}
              containerRef={containerRef}
              onDropdownOptionSelected={searchProps.onDropdownOptionSelected}
              {...restProps}
            />
          ) : (
            <StyledInput
              id="chip-input"
              {...textField}
              multiline
              placeholder={chips.length ? "or type something" : ""}
              {...restProps}
            />
          )}
        </TextAreaContainer>
        {nudge}
        {loading ? (
          <CircularProgress
            color="inherit"
            size={20}
            data-testid="investigation-select-loading"
          />
        ) : (
          <AvailableOptionsContainer>
            {displayedAvailableOptions.map((option) => (
              <Button
                key={option.optionId}
                data-testid={`available-option-${option.optionId}`}
                variant="outlined"
                color="primary"
                onClick={() => {
                  addChip(option);
                  onRecommendedVaccineSelected?.(option);
                }}
                startIcon={<AddIcon />}
              >
                {option.label}
              </Button>
            ))}
            {hasExtraAvailableOptions && (
              <SeeMoreButton
                variant="text"
                color="primary"
                onClick={toggleShowAllAvailableOptions}
              >
                {showAllAvailableOptions ? "See less" : "See more"}
              </SeeMoreButton>
            )}
          </AvailableOptionsContainer>
        )}
      </Container>
    </FormControl>
  );
};
